import { useEffect, useState } from "react";
import { useAddVariant } from "../../../api/recipeSimulator";
import { Button } from "../../../components/ui/common/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "../../../components/ui/common/dialog";
import { Input } from "../../../components/ui/common/input";
import { Label } from "../../../components/ui/common/label";
import { Textarea } from "../../../components/ui/common/textarea";
import { FOCUS_RING_CLASS } from "../../../config/constants";

const VARIANT_NAME_MAX_LENGTH = 15;

interface AddRecipeVariantModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  recipeId: string;
  recipeDataRefetch: () => void;
}

const AddRecipeVariantModal = ({
  open,
  onOpenChange,
  recipeId,
  recipeDataRefetch
}: AddRecipeVariantModalProps) => {
  const [name, setName] = useState("");
  const [notes, setNotes] = useState("");
  const { mutate: addVariant, isLoading } = useAddVariant();

  useEffect(() => {
    if (open) {
      setName("");
      setNotes("");
    }
  }, [open]);

  const trimmedName = name.trim();
  const canSubmit = Boolean(trimmedName) && !isLoading;

  const handleSubmit = () => {
    if (!canSubmit) {
      return;
    }

    addVariant(
      {
        recipeId,
        variant: {
          variantName: trimmedName,
          notes: notes.trim()
        }
      },
      {
        onSuccess: async () => {
          await recipeDataRefetch();
        },
        onSettled: () => {
          onOpenChange(false);
        }
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="tw-max-w-md">
        <DialogHeader>
          <DialogTitle>New Variation</DialogTitle>
          <DialogDescription>
            Give this variation a name and capture any notes that describe how it differs from the
            baseline.
          </DialogDescription>
        </DialogHeader>
        <div className="tw-space-y-4 tw-py-2">
          <div className="tw-space-y-1.5">
            <Label htmlFor="variation-name">Variation name</Label>
            <Input
              id="variation-name"
              value={name}
              onChange={event => setName(event.target.value)}
              placeholder="e.g. Low Fat"
              maxLength={VARIANT_NAME_MAX_LENGTH}
              className={FOCUS_RING_CLASS}
              autoFocus
              disabled={isLoading}
            />
          </div>
          <div className="tw-space-y-1.5">
            <Label htmlFor="variation-notes">Notes</Label>
            <Textarea
              id="variation-notes"
              value={notes}
              onChange={event => setNotes(event.target.value)}
              placeholder="What changes in this variation and why?"
              className={FOCUS_RING_CLASS}
              rows={4}
              disabled={isLoading}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
            Cancel
          </Button>
          <Button disabled={!canSubmit} onClick={handleSubmit}>
            {isLoading ? "Creating..." : "Create Variation"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddRecipeVariantModal;
